import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import {
  AutomaticParkingScheduleClient,
  CheckinUserParkingDTO,
  CheckinUserParkingRegistrationDTO,
  CheckoutUserParkingDTO,
  ParkerRegistrationOffense,
  ParkingProcessInfoClient,
  ParkingProcessInfoDTO,
  ParkingProcessTariffInfoDTO,
  ParkingProcessZoneInfoDTO,
  ParkingQuotaInfoClient,
  ParkingQuotaInfoDTO,
  UserParkingProcessClient,
} from '../../../base/models/models.swagger.generated';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ToastService } from '../../../base/services/toast.service';
import { ParkingProcessInfoService } from '../../../base/services/parking-process-info.service';
import { DateHelper } from '../../../base/shared/date-helper';
import { BitHelper } from '../../../base/shared/bit-helper';
import { DateTime, Duration } from 'luxon';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class ParkingService {

  public parkingProcessInfos: ParkingProcessInfoDTO[];

  constructor(private userParkingProcessClient: UserParkingProcessClient,
              private parkingQuotaInfoClient: ParkingQuotaInfoClient,
              private parkingProcessInfoService: ParkingProcessInfoService,
              private parkingProcessInfoClient: ParkingProcessInfoClient,
              private automaticParkingScheduleClient: AutomaticParkingScheduleClient,
              private toastService: ToastService,
              private spinnerService: SpinnerService,
              private translateService: TranslateService) {
  }

  resolve(): Observable<ParkingProcessInfoDTO[]> {
    return this.loadParkingProcessInfos();
  }

  public checkin(checkinDTO: CheckinUserParkingDTO): Observable<ParkingProcessInfoDTO[]> {
    this.spinnerService.show();
    return this.userParkingProcessClient.checkinUserParking(checkinDTO)
      .pipe(
        tap((registration: CheckinUserParkingRegistrationDTO) => {
          if (registration?.offense != ParkerRegistrationOffense.NoOffense) {
            const offenseTranslation = this.translateService.instant(`i18n.domain.parker-registration.ParkerRegistrationOffense.${ParkerRegistrationOffense[registration.offense]}`);
            this.toastService.showError(
              'i18n.domain.parking.error-messages.check-in-failed.title',
              'i18n.domain.parking.error-messages.check-in-failed.message',
              offenseTranslation
            );
          }
        }),
        switchMap(() => this.loadParkingProcessInfos()),
        tap(() => {
          this.spinnerService.hide();
        }),
        catchError(err => {
          this.spinnerService.hide();
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message');
          return throwError(() => err);
        }),
      );
  }

  public checkout(checkoutDTO: CheckoutUserParkingDTO): Observable<ParkingProcessInfoDTO[]> {
    this.spinnerService.show();
    return this.userParkingProcessClient.checkoutUserParking(checkoutDTO)
      .pipe(
        switchMap(() => this.loadParkingProcessInfos()),
        tap(() => {
          this.spinnerService.hide();
        }),
        catchError(err => {
          this.spinnerService.hide();
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message');
          return throwError(() => err);
        }),
      );
  }

  public loadParkingProcessInfos(): Observable<ParkingProcessInfoDTO[]> {
    return this.parkingProcessInfoService.getParkingProcessInfos().pipe(
      tap((parkingProcessInfos: ParkingProcessInfoDTO[]) => {
        this.parkingProcessInfos = parkingProcessInfos?.length > 0 ? parkingProcessInfos : null;
      })
    );
  }

  public getParkingProcessZoneInfos(clientId: number): Observable<ParkingProcessZoneInfoDTO[]> {
    return this.parkingProcessInfoClient.getParkingProcessZoneInfos(clientId, false);
  }

  public getAutomaticParkingScheduleZoneInfos(clientId: number): Observable<ParkingProcessZoneInfoDTO[]> {
    return this.automaticParkingScheduleClient.getAutomaticParkingScheduleZoneInfos(clientId)
      .pipe(
        map(zoneInfo => zoneInfo.map(zone => ({
          ...zone,
          subscriptionAssignments: zone.subscriptionAssignments.map(assignment => ({
            ...assignment,
            subscription: {
              ...assignment.subscription,
              tariffs: this.createOvernightTariffsIfNeeded(assignment.subscription.tariffs),
            },
          })),
        })) as ParkingProcessZoneInfoDTO[]),
      );
  }

  public getParkingQuotaInfo(subscriptionId: number): Observable<ParkingQuotaInfoDTO> {
    return this.parkingQuotaInfoClient.getParkingQuotaInfo(subscriptionId, DateTime.utc());
  }

  private createOvernightTariffsIfNeeded(tariffs: ParkingProcessTariffInfoDTO[]): ParkingProcessTariffInfoDTO[] {
    /* eslint-disable no-bitwise*/
    const tempTariffs = [];
    // check if we need to add extra tariffs that overlap days
    tariffs.forEach(t => {
      if (t.isTwentyFourSevenTariff) {
        return;
      }
      const endDateTime = DateHelper.setDateTimeTo1970FromDuration(t.startTime).plus({minutes: t.durationInMinutes});

      if (endDateTime.day > 1) {
        const startTime = DateHelper.getDateTimeStart1970().plus({day: 1});
        const duration = endDateTime.diff(startTime, 'minutes').minutes;

        if (duration > 0) {
          let onWeekdays = t.onWeekDays << 1; // shift bit one left
          if (BitHelper.isBitSet(t.onWeekDays, 7)) { // if "day" after sunday is set (8th bit)
            onWeekdays = onWeekdays & ~1 << 7; // unset "day" after sunday
            onWeekdays = onWeekdays | 1 << 0; // set monday
          }

          const tempTariff = new ParkingProcessTariffInfoDTO({
            id: 0,
            onWeekDays: onWeekdays,
            startTime: Duration.fromDurationLike(0),
            durationInMinutes: duration,
            isTwentyFourSevenTariff: false,
            tariffCurrency: t.tariffCurrency
          });

          tempTariffs.push(tempTariff);
        }
      }
    });

    return [...tariffs, ...tempTariffs];
    /* eslint-enable no-bitwise*/
  }
}
